// Feed cache
// ----------
// Every tab used to refetch from scratch on every visit, so switching tabs —
// or coming back to the one you started on — meant watching a spinner for
// content that had been on screen thirty seconds earlier.
//
// This holds what each feed last loaded, so a revisit renders immediately.
//
// In memory and per session on purpose. A dating feed is not something to
// persist to disk: it goes stale, it is personal, and the cost of a miss is
// one request. It clears itself when the tab is closed, which is right.
//
// Entries keep the loop cursor alongside the items. Without it a returning
// viewer would render the cached list and then page from offset zero on the
// next scroll, quietly serving the first twenty people a second time.

import { LoopCursor } from './feedLoop';

/**
 * How long an entry is served without question.
 *
 * Long enough to cover moving around the tabs, short enough that a feed left
 * open in a background tab is not still showing this morning's people. New
 * candidates do not appear minute to minute, so there is nothing to gain from
 * revalidating more eagerly than this.
 */
const TTL_MS = 5 * 60 * 1000;

export interface CachedFeed<T> {
  items: T[];
  cursor: LoopCursor;
  /** Anything a feed needs to restore beyond its items. */
  meta?: Record<string, unknown>;
  /** Written at, in ms. */
  at: number;
}

const store = new Map<string, CachedFeed<unknown>>();

/**
 * Cache key for one feed.
 *
 * `variant` distinguishes results that are not interchangeable — Nearby ranked
 * from a device position is a different list from Nearby ranked from the
 * profile address, and serving one where the other is expected would silently
 * show the wrong distances.
 */
export const feedCacheKey = (surface: string, userId: string, variant = 'default'): string =>
  `${surface}:${userId}:${variant}`;

export function readFeedCache<T>(key: string): CachedFeed<T> | null {
  const entry = store.get(key) as CachedFeed<T> | undefined;
  if (!entry) return null;
  if (Date.now() - entry.at > TTL_MS) {
    store.delete(key);
    return null;
  }
  return entry;
}

export function writeFeedCache<T>(
  key: string,
  entry: Omit<CachedFeed<T>, 'at'>
): void {
  store.set(key, { ...entry, at: Date.now() } as CachedFeed<unknown>);
}

/**
 * The freshest entry for a surface, whichever variant it was stored under.
 *
 * Nearby needs this: its key depends on the device position, and the position
 * takes a moment to resolve. Rather than make the viewer watch a spinner while
 * the browser thinks about geolocation, the tab renders whatever it has and
 * corrects itself if the resolved position turns out to belong to a different
 * list.
 */
export function readAnyFeedCache<T>(
  surface: string,
  userId: string
): { key: string; entry: CachedFeed<T> } | null {
  const prefix = `${surface}:${userId}:`;
  let best: { key: string; entry: CachedFeed<T> } | null = null;

  store.forEach((entry, key) => {
    if (!key.startsWith(prefix)) return;
    const fresh = readFeedCache<T>(key);
    if (!fresh) return;
    if (!best || fresh.at > best.entry.at) best = { key, entry: fresh };
  });

  return best;
}

export function hasFreshFeedCache(key: string): boolean {
  return readFeedCache(key) !== null;
}

/** Drops one entry, or everything when called bare. Used by pull-to-refresh. */
export function clearFeedCache(key?: string): void {
  if (key) store.delete(key);
  else store.clear();
}
